import { useMemo } from "react";
import { useT } from "@/i18n";
import type { FilterDefs } from "@/hooks/useFilterSet";
import { useSelectableClients } from "@/features/clients/hooks";
import { useOwners, useStages } from "./hooks";

/**
 * The pipeline's filter vocabulary, defined once for both views.
 *
 * The board and the list used to declare their own filter sets — the board four inline
 * selects, the list a different four plus a free-text box for a client **UUID** — which
 * meant the same URL offered different controls depending on which view you were in. One
 * module is what makes "the view changes presentation, never the data" structurally true
 * rather than a promise in a spec.
 *
 * Options come from the API, so the stages and the real owners are never duplicated as
 * literals in the front end — and once stages are configurable, a renamed stage relabels
 * the filter without a deployment.
 *
 * Year is deliberately **absent**: it is a pipeline-wide scope in the header, not a
 * filter, because the board cannot render "all years" and a dismissable year chip would
 * therefore mean something different on each view.
 */
export function useOpportunityFilterDefs(): FilterDefs {
  const t = useT();
  const stages = useStages();
  const owners = useOwners();
  // The empty term asks for the whole selectable set; the board has always fetched it
  // this way, and the list needs the same list to offer the same options.
  const clients = useSelectableClients("");

  const stageOptions = stages.data;
  const ownerOptions = owners.data;
  const clientOptions = clients.data;

  // Explicitly generic: without it the literal `type` fields widen to `string` and the
  // discriminated union stops discriminating.
  return useMemo<FilterDefs>(
    () => ({
      q: { type: "search" },
      owner: {
        type: "multi",
        label: t("filter.owner"),
        options: (ownerOptions ?? []).map((owner) => ({ value: owner.id, label: owner.name })),
      },
      client: {
        type: "multi",
        label: t("filter.client"),
        options: (clientOptions ?? []).map((client) => ({ value: client.id, label: client.name })),
      },
      stage: {
        type: "multi",
        label: t("filter.stage"),
        options: (stageOptions ?? []).map((stage) => ({ value: stage.id, label: stage.name })),
      },
      quarter: {
        type: "multi",
        label: t("filter.quarter"),
        options: [1, 2, 3, 4].map((quarter) => ({
          value: String(quarter),
          label: t("filter.quarterOption", quarter),
        })),
      },
      /**
       * Stalled, as a filter anybody can reach (2026-08-26).
       *
       * It was `hidden` — a chip with no control — which made the Stalled tile's click land on
       * a filter the panel could not show or undo: a phantom. It is the one analytics landing
       * param no phase set can express (a stalled deal is stalled *within* its phase), so
       * rather than removing it, it is now a two-value select like any other filter.
       */
      stalled: { type: "bool", label: t("filter.stalledOnly") },
    }),
    [ownerOptions, clientOptions, stageOptions, t],
  );
}
